/** Copyright (c) 2020 T.Rowe Price. All rights reserved. */

import { isEmpty, isNil } from 'lodash';
import { createSelector } from 'reselect';

import {
  getAccounts,
  getActivePlusAccounts
} from 'state/account/AccountSelectors';
import {
  isBrokerage,
  isActiveAdvisory,
  isActivePlus,
  isCollegeSavings
} from 'services/Products';
import { extractAndFormatOwners } from 'services/Accounts';
import { getProductTypeClass } from 'services/Products';
import { EMPTY_CELL_DASH } from 'services/TableUtils';
import { accountName } from 'services/Formats';
import {
  getActivePlusBenchmarks,
  getModelBenchmark
} from 'state/performance/ProductPerformanceSelectors';
import { getPerformancePeriods } from 'state/performance/PerformanceSelectors';
import { getRateOfReturnFilter } from '../performance/RateOfReturnFilterSelectors';
import { applyPerformanceFilters } from '../performance/PerformancePageUtils';
import { getPerformancePage } from '../performance/PerformancePageSelectors';

export const getAccountPerformanceMap = (state) => state.accountPerformanceMap;
export const inceptionReturn = (state) =>
  state.accountPerformanceAggregate
    ? state.accountPerformanceAggregate.inceptionReturn
    : 0;

export const getActivePlusAccountPerformanceData = createSelector(
  getActivePlusAccounts,
  getAccountPerformanceMap,
  (activePlusAccounts = [], performanceMap) => {
    if (isEmpty(performanceMap)) {
      return [];
    }
    return activePlusAccounts.map(
      (account) => performanceMap[account.accountId] || {}
    );
  }
);

export const getRateOfReturnSummary = createSelector(
  getAccounts,
  getAccountPerformanceMap,
  getModelBenchmark,
  (visibleAccounts, performanceMap, rasModelBenchmark) => {
    if (isEmpty(performanceMap)) {
      return [];
    }

    return visibleAccounts
      .filter((account) => !isBrokerage(account.productType))
      .filter(
        (account) =>
          !isActiveAdvisory(account.productType) ||
          (isActiveAdvisory(account.productType) &&
            !isNil(performanceMap[account.accountId]))
      )
      .map((account) => {
        const {
          accountId,
          productType,
          accountType,
          accountNumber,
          accountChecksum
        } = account;

        // Group all 529s under collegeSavings
        const productClass = isCollegeSavings(productType)
          ? 'collegeSavings'
          : getProductTypeClass(productType);

        const rasBenchmarkRow =
          isActiveAdvisory(productType) && !isNil(rasModelBenchmark[0])
            ? rasModelBenchmark[0].benchmark
            : null;
        const managementType = isActiveAdvisory(productType);
        const ownerNames = extractAndFormatOwners(account);
        const typeAndOwners = isEmpty(ownerNames)
          ? `${accountType}`
          : `${accountType}\n${ownerNames}`;
        const accountDescriptor = accountName(account);
        const accountNameAndNumber = `${accountDescriptor}\n${accountNumber}`;
        const accountNumberWChecksum = `${accountNumber}-${accountChecksum}`;

        const performance = performanceMap[accountId] || {};

        return {
          ...account,
          ...performance,
          rasBenchmarkRow,
          productClass,
          managementType,
          accountDescriptor,
          typeAndOwners,
          accountNumber,
          accountNameAndNumber,
          accountNumberWChecksum,
          hideButtons: false,
          disableAccountLink: false,
          disableAsOf: true
        };
      });
  }
);

export const getOpenRateOfReturnSummary = createSelector(
  getRateOfReturnSummary,
  getActivePlusBenchmarks,
  getRateOfReturnFilter,
  getPerformancePage,
  (summary, benchmarks, performanceFilter, performancePage) => {
    const filteredAccounts = applyPerformanceFilters(
      performancePage,
      performanceFilter,
      summary
    );

    return filteredAccounts
      .filter((account) => !account.closedAccount && !account.purgedAccount)
      .reduce((accounts, account) => {
        account.personalInvestment = 'Personal Investments';
        if (isActivePlus(account.productType)) {
          const benchmark = benchmarks[account.accountId]
            ? benchmarks[account.accountId][0]
            : [];

          if (!isEmpty(benchmark)) {
            for (const key in benchmark) {
              if (isNil(benchmark[key])) {
                benchmark[key] = EMPTY_CELL_DASH;
              }
            }
          }

          const appBenchmark = {
            ...account,
            ...benchmark,
            accountDescriptor: 'Model Benchmark',
            accountNumber: '',
            accountNumberWChecksum: '',
            isViewOnly: false,
            sweepAccount: false,
            balanceAsOfDate: null,
            hideButtons: true,
            disableAccountLink: true
          };

          return [...accounts, account, appBenchmark];
        }

        return [...accounts, account];
      }, []);
  }
);

export const getClosedRateOfReturnSummary = createSelector(
  getRateOfReturnSummary,
  getRateOfReturnFilter,
  getPerformancePage,
  (summary, performanceFilter, performancePage) => {
    const filteredAccounts = applyPerformanceFilters(
      performancePage,
      performanceFilter,
      summary
    );
    return filteredAccounts
      .filter((account) => account.closedAccount)
      .map((account) => ({
        ...account,
        hideButtons: true,
        disableAccountLink: true
      }));
  }
);

export const getFilteredPerformancePeriods = createSelector(
  getPerformancePeriods,
  getRateOfReturnFilter,
  getRateOfReturnSummary,
  getPerformancePage,
  (performancePeriods, performanceFilter, summary, performancePage) => {
    const filteredAccounts = applyPerformanceFilters(
      performancePage,
      performanceFilter,
      summary
    );

    if (
      (!isEmpty(filteredAccounts) &&
        filteredAccounts.some((account) =>
          isActiveAdvisory(account.productType)
        )) ||
      filteredAccounts.filter((account) => isActivePlus(account.productType))
        .length === 1
    ) {
      const advicePeriods = [
        {
          key: 'monthToDateRateOfReturn',
          label: 'Month-to-Date'
        },
        {
          key: 'quarterToDateRateOfReturn',
          label: 'Quarter-to-Date'
        },
        ...performancePeriods
      ];

      return filteredAccounts.every(
        (account) =>
          isActiveAdvisory(account.productType) ||
          isActivePlus(account.productType)
      )
        ? advicePeriods.filter((period) => period.key !== 'lastQuarterReturn')
        : advicePeriods;
    }

    return performancePeriods;
  }
);
